import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { NgxDatatableModule, ColumnMode } from '@swimlane/ngx-datatable';
import { Router } from '@angular/router';
import { Page, Pagination } from '../../../shared/models/page';
import {
  VisitPlan,
  VisitPlanFilters,
  VisitPlanFilterOptions,
  VisitPlanStatus,
  getStepNumber,
  hasReachedStep,
} from '../models/visit-plan';
import { VisitPlanService } from '../../../core/services/visit-plan.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { finalize, Subject, switchMap } from 'rxjs';
import { NgxPermissionsModule } from 'ngx-permissions';
import { AlertType } from '../../../shared/models/alertType';
import { showAlertDialog } from '../../../shared/helpers/showAlertDialog';
import { BadgeComponent } from '../../../shared/components/badge/badge.component';
import {
  SearchFilterBarComponent,
  FilterConfig,
  FilterValues,
} from '../../../shared/components/search-filter-bar/search-filter-bar.component';

@Component({
  selector: 'app-roteiros-lista',
  standalone: true,
  imports: [
    MatIconModule,
    CommonModule,
    NgxDatatableModule,
    MatTooltipModule,
    NgxPermissionsModule,
    BadgeComponent,
    SearchFilterBarComponent,
  ],
  templateUrl: './roteiros-lista.component.html',
  styleUrl: './roteiros-lista.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RoteirosListaComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private loadPageSubject = new Subject<VisitPlanFilters>();
  visitPlans: VisitPlan[] = [];
  page: Page = { pageSize: 10, page: 0, count: 0 };
  ColumnMode = ColumnMode;

  filters: FilterConfig[] = [];

  private currentSearch = '';
  private currentFilters: Record<string, string> = {};

  isLoading = signal(false);

  constructor(
    private visitPlanService: VisitPlanService,
    private dialog: MatDialog,
    private router: Router
  ) {}

  ngOnInit() {
    this.initializeFilters();

    this.loadPageSubject
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((filters: VisitPlanFilters) => {
          this.isLoading.set(true);
          return this.visitPlanService.getList(filters).pipe(
            finalize(() => this.isLoading.set(false))
          );
        })
      )
      .subscribe((pagedData: Pagination<VisitPlan>) => {
        this.page.count = pagedData.totalCount;
        this.visitPlans = pagedData.items;
      });

    this.loadPage();
  }

  private initializeFilters(): void {
    this.filters = this.buildFilters();

    this.visitPlanService.getFilterOptions().subscribe({
      next: (options: VisitPlanFilterOptions) => {
        this.filters = this.buildFilters(options);
      },
    });
  }

  private buildFilters(options?: VisitPlanFilterOptions): FilterConfig[] {
    const stateOptions = options?.states.map((s) => ({ value: s, label: s })) ?? [];
    const cityOptions = options?.cities.map((c) => ({ value: c, label: c })) ?? [];

    return [
      {
        key: 'routeCount',
        placeholder: 'Quantidade de Rotas',
        options: [
          { value: '1-5', label: '1 a 5 rotas' },
          { value: '6-10', label: '6 a 10 rotas' },
          { value: '11-20', label: '11 a 20 rotas' },
          { value: '21+', label: 'Mais de 20 rotas' },
        ],
        minWidth: '12rem',
      },
      {
        key: 'state',
        placeholder: 'Estados Abrangidos',
        options: stateOptions,
        minWidth: '10rem',
      },
      {
        key: 'cityName',
        placeholder: 'Cidades Abrangidas',
        options: cityOptions,
        minWidth: '12rem',
      },
      {
        key: 'status',
        placeholder: 'Status',
        options: [
          { value: 'Draft', label: 'Rascunho' },
          { value: 'Published', label: 'Publicado' },
          { value: 'Archived', label: 'Arquivado' },
        ],
        minWidth: '8rem',
      },
    ];
  }

  onFilterChange(event: FilterValues): void {
    this.currentSearch = event.search;
    this.currentFilters = event.filters;
    this.page.page = 0;
    this.loadPage();
  }

  onPage(event: { offset?: number; page?: number }) {
    if (event.page !== undefined) {
      this.page.page = event.page - 1;
    } else if (event.offset !== undefined) {
      this.page.page = event.offset;
    }
    this.loadPage();
  }

  loadPage() {
    const routeCountRange = this.parseRouteCountRange(this.currentFilters['routeCount']);

    const filters: VisitPlanFilters = {
      sort: 'normal',
      page: this.page.page + 1,
      pageSize: this.page.pageSize,
      search: this.currentSearch.trim() || undefined,
      status: (this.currentFilters['status'] as VisitPlanStatus) || undefined,
      minRouteCount: routeCountRange.min,
      maxRouteCount: routeCountRange.max,
      cityName: this.currentFilters['cityName'] || undefined,
      state: this.currentFilters['state'] || undefined,
    };
    this.loadPageSubject.next(filters);
  }

  private parseRouteCountRange(value: string | undefined): { min?: number; max?: number } {
    if (!value) return {};

    switch (value) {
      case '1-5':
        return { min: 1, max: 5 };
      case '6-10':
        return { min: 6, max: 10 };
      case '11-20':
        return { min: 11, max: 20 };
      case '21+':
        return { min: 21 };
      default:
        return {};
    }
  }

  getStatusVariant(status: VisitPlanStatus): 'green' | 'yellow' | 'gray' {
    switch (status) {
      case 'Published':
        return 'green';
      case 'Draft':
        return 'yellow';
      case 'Archived':
        return 'gray';
      default:
        return 'gray';
    }
  }

  getStatusLabel(status: VisitPlanStatus): string {
    switch (status) {
      case 'Published':
        return 'Publicado';
      case 'Draft':
        return 'Rascunho';
      case 'Archived':
        return 'Arquivado';
      default:
        return status;
    }
  }

  getStartIndex(): number {
    if (this.page.count === 0) return 0;
    return this.page.page * this.page.pageSize + 1;
  }

  getEndIndex(): number {
    const end = (this.page.page + 1) * this.page.pageSize;
    return Math.min(end, this.page.count);
  }

  navigateToEdit(id: number): void {
    this.router.navigate(['/roteirizacao/editar', id]);
  }

  navigateToNew(): void {
    this.router.navigate(['/roteirizacao/novo']);
  }

  onRowActivate(event: { type: string; row: VisitPlan }): void {
    if (event.type === 'click') {
      this.navigateToEditWithStep(event.row);
    }
  }

  navigateToEditWithStep(visitPlan: VisitPlan): void {
    const basePath = `/roteirizacao/editar/${visitPlan.id}`;
    const stepNumber = getStepNumber(visitPlan.lastStep);

    let path = basePath;
    if (hasReachedStep(stepNumber, 'CityReview') && !hasReachedStep(stepNumber, 'RouteGeneration')) {
      path = `${basePath}/revisar-cidades`;
    } else if (hasReachedStep(stepNumber, 'RouteGeneration')) {
      path = `${basePath}/visualizar-rotas`;
    }

    this.router.navigate([path]);
  }

  confirmDelete(visitPlan: VisitPlan): void {
    const alertDialogRef = showAlertDialog(
      this.dialog,
      AlertType.Warning,
      'Atenção!',
      `Você tem certeza que deseja excluir este roteiro? Esta ação não poderá ser desfeita.`,
      'Confirmar'
    );

    alertDialogRef.afterClosed().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((result: boolean) => {
      if (result) {
        this.isLoading.set(true);
        this.visitPlanService.deleteVisitPlan(visitPlan.id).pipe(
          finalize(() => this.isLoading.set(false))
        ).subscribe({
          next: () => {
            this.loadPage();
            showAlertDialog(
              this.dialog,
              AlertType.Success,
              'Sucesso!',
              'Roteiro excluído com sucesso!'
            );
          },
          error: () => {
            showAlertDialog(
              this.dialog,
              AlertType.Error,
              'Erro',
              'Não foi possível excluir o roteiro. Tente novamente mais tarde.'
            );
          },
        });
      }
    });
  }
}
